import { ShadowCard } from 'components/Common/ShadowCard';
import { FieldArray, useFormikContext } from 'formik';
import React, {
  useMemo,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion } from 'components/Common/Accordion';
import { ApplicantAdd } from './ApplicantAdd';
import { ApplicantBody } from '../Tabs/ApplicantBody';
import { ApplicantDetail } from '../Tabs/ApplicantDetail';
import { typeColor } from 'helpers/styles/typeColor';
import { useOrderAlert } from 'hooks/useOrderAlert';
import { isUserEmployee, isUserClient } from 'helpers/jwt_helper';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PREVIOUS_VISA_STATE } from '../Tabs/ApplicantMainInfo';
import { orderApplicantOrderFormDownloadRequest } from 'helpers/api-requests/admin';
import { showToastError } from 'helpers/utils/toast';
import { downloadDocument } from 'helpers/downloadDocument';
import { ACCORDION_THEMES } from 'common/data/accordion';

export const OrderApplicants = forwardRef(
  (
    { useFieldPreviousVisa, useFieldPreviousUssr, orderTypeFieldsSettings },
    ref,
  ) => {
    const formik = useFormikContext();
    const { id } = useParams();
    const { t } = useTranslation();
    const isApplicantSetOneTime = useRef(false);
    const { openAlert } = useOrderAlert();

    // Create refs array for DocumentsReceived components
    const applicantsRefs = useRef([]);
    const updateAllOriginalValues = () => {
      // Loop through each applicant and call their updateOriginalValue function
      applicantsRefs.current.forEach(applicantRef => {
        applicantRef?.updateOriginalValue();
      });
    };

    useImperativeHandle(ref, () => ({
      updateAllOriginalValues: updateAllOriginalValues,
    }));

    const [applicantMaxQuantity, setApplicantMaxQuantity] = useState(1);
    useEffect(
      () =>
        setApplicantMaxQuantity(orderTypeFieldsSettings?.applicantMaxQuantity),
      [orderTypeFieldsSettings?.applicantMaxQuantity],
    );

    const [applicantCount, setApplicantCount] = useState(0);

    useEffect(() => {
      setApplicantCount(formik.values?.applicants?.length);
    }, [formik.values?.applicants]);

    const isDisabledFields = useMemo(
      () => !formik.values.orderDetail?.isEditable,
      [formik.values.orderDetail?.isEditable],
    );

    const [isOpenAddApplicant, setIsOpenAddApplicant] = useState(false);

    const getIsOpenAddApplicant = () => {
      let baseCond = null;

      if (!formik.values.orderType) {
        return false;
      }

      if (isUserEmployee) {
        baseCond = orderTypeFieldsSettings?.enableCountry
          ? !!formik.values.client.country
          : true;
        return baseCond && applicantCount < 1;
      }

      if (isUserClient) {
        baseCond = orderTypeFieldsSettings?.enableCountry
          ? !!formik.values.client.country
          : true;
        return (
          baseCond && applicantCount < applicantMaxQuantity && !isDisabledFields
        );
      }

      // operator & admin
      if (orderTypeFieldsSettings?.enableCountry) {
        baseCond =
          !!formik.values.client.country &&
          !!formik.values.client.clientType &&
          !!formik.values.client.client;
      } else {
        baseCond =
          formik.values.client.clientType && !!formik.values.client.client;
      }
      return applicantCount < applicantMaxQuantity && baseCond;
    };

    useEffect(() => {
      setIsOpenAddApplicant(getIsOpenAddApplicant);
    }, [
      orderTypeFieldsSettings?.enableCountry,
      applicantMaxQuantity,
      formik.values.client.country,
      formik.values.client.client,
      formik.values.clientType,
      applicantCount,
    ]);

    const handleCustomerOrderFormDownload = async (customer, fileType) => {
      try {
        const response = await orderApplicantOrderFormDownloadRequest(
          id,
          customer.customerId,
          fileType,
          {
            responseType: 'blob',
          },
        );

        downloadDocument(
          response,
          `Order-${id}-${customer.applicantDetail.firstName} ${customer.applicantDetail.lastName}-${fileType}.pdf`,
        );
      } catch (err) {
        showToastError(err.message);
      }
    };

    useEffect(() => {
      if (
        !id &&
        formik.values.applicants.length === 0 &&
        isOpenAddApplicant &&
        !isApplicantSetOneTime.current
      ) {
        formik.setFieldValue('applicants', [
          {
            services: [],
            applicantDetail: { firstname: null },
            statusApplicant: 'draft',
            files: [],
            previusVisa: PREVIOUS_VISA_STATE[0].value,
          },
        ]);

        isApplicantSetOneTime.current = true;
      }
    }, [id, formik.values.applicants, isOpenAddApplicant]);

    const [addApplicantBlockTitle, setApplicantBlockTitle] = useState();

    useEffect(() => {
      setApplicantBlockTitle(
        orderTypeFieldsSettings?.enableContactPerson
          ? t('order.addContactPersonData')
          : formik.values?.applicants?.length === 1
          ? t('order.moreApplicant')
          : t('order.addApplicantData'),
      );
    }, [applicantMaxQuantity, formik.values?.applicants]);

    return (
      <FieldArray
        name="applicants"
        render={({ push, remove }) => (
          <>
            {formik.values?.applicants?.length > 0 &&
              formik.values.applicants.map((applicant, index) => {
                const fullName =
                  applicant.applicantDetail.firstName &&
                  applicant.applicantDetail.lastName
                    ? `${applicant.applicantDetail.firstName} ${applicant.applicantDetail.lastName}`
                    : `${t('common.applicant')} ${index + 1}`;

                return (
                  <ShadowCard
                    id={`applicant-${index + 1}`}
                    className="mb-4"
                    // bodyStyle={{ padding: '0px' }}
                    style={{ scrollMargin: '-110px' }}
                    key={applicant?.applicantDetail?.id ?? index}
                  >
                    <Accordion
                      title={fullName}
                      theme={ACCORDION_THEMES.PRIMARY}
                      icon="bx bx-user"
                      // status={applicantStatus}
                      showBadge={true}
                      id={`applicant-accordion-${index}`}
                      defaultOpen
                    >
                      {/* ApplicantDetail with all its buttons */}
                      <div>
                        <ApplicantDetail
                          applicant={applicant.applicantDetail}
                          index={index}
                          removeApplicant={() => {
                            remove(index);
                            setApplicantCount(applicantCount - 1);
                          }}
                          orderTypeFieldsSettings={orderTypeFieldsSettings}
                          setApplicantCount={setApplicantCount}
                          applicantData={applicant}
                          onDownload={handleCustomerOrderFormDownload}
                          isDisabledFields={isDisabledFields}
                        />
                      </div>

                      {/* ApplicantBody */}
                      <div>
                        {applicant.applicantDetail.firstName && (
                          <ApplicantBody
                            ref={el => (applicantsRefs.current[index] = el)}
                            useFieldPreviousVisa={useFieldPreviousVisa}
                            useFieldPreviousUssr={useFieldPreviousUssr}
                            index={index}
                            orderTypeFieldsSettings={orderTypeFieldsSettings}
                          />
                        )}
                      </div>
                    </Accordion>
                  </ShadowCard>
                );
              })}

            {isOpenAddApplicant && (
              <ApplicantAdd
                handleCreateApplicant={() => {
                  setApplicantCount(applicantCount + 1);
                  push({
                    services: [],
                    applicantDetail: { firstname: null },
                    statusApplicant: 'draft',
                    files: [],
                    previusVisa: PREVIOUS_VISA_STATE[0].value,
                  });
                  openAlert({
                    message: 'order.addApplicantNotification',
                    type: typeColor.success,
                  });
                }}
                title={addApplicantBlockTitle}
              />
            )}
          </>
        )}
      />
    );
  },
);
